import React, { useState, useEffect } from "react";
import { SearchIcon, RotateCw, X, FilterX, Plus } from "lucide-react";
import { NavLink } from "react-router-dom";
import {
  CircularProgress,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import RoleCard from "./Components/RoleCard";
import { Permissions } from "../../../permissions";
import { dummyRoles } from "../../../dummyData";
import BreadCrumbsContainer from "../../Common/Breadcrumbs";

const Role = () => {
  const [roles, setRoles] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    permissionGroup: "",
    sortOrder: "asc", // "asc" or "desc"
  });

  // Check user permissions
  const canCreateRole = Permissions.includes("create_role");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500); // Wait 500ms after last keystroke

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load role data
  useEffect(() => {
    fetchRoles();
  }, []);

  // Filter roles based on search query and filters
  useEffect(() => {
    let filtered = [...roles];

    // Apply search filter
    if (debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase();
      filtered = filtered.filter(
        role =>
          role.name.toLowerCase().includes(query) ||
          role.description.toLowerCase().includes(query) ||
          role.permissionGroups.some(group =>
            group.toLowerCase().includes(query),
          ),
      );
    }

    // Apply permission group filter
    if (filters.permissionGroup) {
      filtered = filtered.filter(role =>
        role.permissionGroups.includes(filters.permissionGroup),
      );
    }

    // Apply sorting
    filtered = filtered.sort((a, b) => {
      if (filters.sortOrder === "asc") {
        return a.name.localeCompare(b.name);
      } else {
        return b.name.localeCompare(a.name);
      }
    });

    setFilteredRoles(filtered);
  }, [debouncedSearch, roles, filters]);

  const fetchRoles = async () => {
    setIsLoading(true);
    try {
      // In a real app, fetch from API
      // const response = await api.get('/roles');
      // const data = response.data;

      // Using dummy data for demo
      await new Promise(resolve => setTimeout(resolve, 500));
      const data = dummyRoles;

      setRoles(data);
      setFilteredRoles(data);
    } catch (err) {
      console.error("Error fetching roles:", err);
      setError("Failed to load roles. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle refreshing the data
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchRoles();
      // In a real app, you might show a success toast
      // successToast("Role list refreshed");
    } catch (err) {
      // In a real app, you might show an error toast
      // errorToast("Failed to refresh role list");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle search input change
  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  // Clear search input
  const clearSearch = () => {
    setSearchQuery("");
  };

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery("");
    setFilters({
      permissionGroup: "",
      sortOrder: "asc",
    });
  };

  // Handle role deletion
  const handleDeleteRole = async roleId => {
    try {
      // In a real app, call API to delete
      // await api.delete(`/roles/${roleId}`);

      // Mock deletion
      await new Promise(resolve => setTimeout(resolve, 800));

      // Update local state
      const updatedRoles = roles.filter(role => role.id !== roleId);
      setRoles(updatedRoles);
    } catch (err) {
      console.error("Error deleting role:", err);
      // In a real app, show an error toast notification
    }
  };

  // Get unique permission groups for filter
  const permissionGroups = [
    ...new Set(roles.flatMap(role => role.permissionGroups)),
  ];

  return (
    <div>
      <div className="bg-white h-[calc(100vh-120px)]">
        {/* Header Section */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Roles
          </h1>

          {/* Search and Action Buttons */}
          <div className="flex items-center gap-4">
            {/* Search Bar */}
            <div className="w-[550px]">
              <div className="relative">
                <SearchIcon
                  className="absolute left-3 top-1/2 transform -translate-y-1/2"
                  size={18}
                  color="#83899F"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search by role name, description..."
                  className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                           focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                           outline-none transition-all text-gray-600"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:bg-gray-100 p-1 rounded-full">
                    <X size={16} color="#83899F" />
                  </button>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {canCreateRole && (
                <NavLink
                  to="/usermanagement/createrole"
                  className="inline-flex items-center px-4 py-2 bg-[#5652B0] text-white rounded-lg hover:bg-[#4A47A3] transition-all text-sm font-medium">
                  <Plus size={16} className="mr-2" />
                  <span className="font-medium">Create new Role</span>
                </NavLink>
              )}
              <IconButton
                onClick={handleRefresh}
                className="hover:bg-gray-100"
                disabled={isRefreshing}
                title="Refresh">
                <RotateCw
                  size={20}
                  className={`text-gray-600 ${
                    isRefreshing ? "animate-spin" : ""
                  }`}
                />
              </IconButton>
            </div>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <Alert severity="error" className="mb-6">
              {error}
            </Alert>
          )}

          {/* Filters */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4 flex-wrap">
              {/* Permission Group Filter */}
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Permission Group</InputLabel>
                <Select
                  value={filters.permissionGroup}
                  label="Permission Group"
                  onChange={e =>
                    handleFilterChange("permissionGroup", e.target.value)
                  }
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5652B0",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5652B0",
                    },
                  }}>
                  <MenuItem value="">All Permission Groups</MenuItem>
                  {permissionGroups.map(group => (
                    <MenuItem key={group} value={group}>
                      {group}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Sort Order */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={filters.sortOrder}
                  label="Sort By"
                  onChange={e =>
                    handleFilterChange("sortOrder", e.target.value)
                  }
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#E5E7EB",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5652B0",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#5652B0",
                    },
                  }}>
                  <MenuItem value="asc">Name (A-Z)</MenuItem>
                  <MenuItem value="desc">Name (Z-A)</MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* Clear Filters Button */}
            {(searchQuery || filters.permissionGroup !== "") && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 
                         hover:bg-gray-100 rounded-lg transition-colors">
                <FilterX size={16} />
                Clear Filters
              </button>
            )}
          </div>

          {/* Role cards content */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16 bg-gray-50 rounded-lg">
              <CircularProgress size={40} sx={{ color: "#5652B0" }} />
              <p className="mt-4 text-gray-500 font-outfit">Loading roles...</p>
            </div>
          ) : filteredRoles.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredRoles.map(role => (
                <RoleCard
                  key={role.id}
                  role={role}
                  onDelete={handleDeleteRole}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
              <p className="text-gray-500 mb-2 font-outfit">No roles found</p>
              {(searchQuery || filters.permissionGroup !== "") && (
                <p className="text-sm text-gray-400 font-outfit">
                  Try adjusting your search filters
                </p>
              )}
              {canCreateRole &&
                !searchQuery &&
                filters.permissionGroup === "" && (
                  <NavLink
                    to="/usermanagement/createrole"
                    className="inline-flex items-center px-4 py-2 mt-4 bg-[#5652B0] text-white rounded-lg hover:bg-[#4A47A3] transition-all">
                    <Plus size={16} className="mr-2" />
                    <span className="font-medium">Create your first role</span>
                  </NavLink>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Role;
